import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { RulesService } from '../api/services/rules.service';
import {
    AdditionalRuleModelPagedResponseModel,
    DrainageVentilationRuleModelPagedResponseModel,
    GlazingRuleModelPagedResponseModel,
    IntegratedRuleModelPagedResponseModel,
    JointRuleModelPagedResponseModel,
    LimitationRuleModelPagedResponseModel,
    MatingRuleModelPagedResponseModel,
    QueryParams
} from '../api/models';
import { AuthService } from '../shared/auth-service/auth.service';
import { AppToastService } from '../shared/toast-service/toast-service';
import { Router } from '@angular/router';
import { ChartConfiguration, ChartData, Chart, registerables } from 'chart.js';

// Register Chart.js components
Chart.register(...registerables);

interface RuleStatistics {
    ruleType: string;
    totalCount: number;
    lastUpdated?: Date;
}

interface RuleTypeFilter {
    ruleType: string;
    selected: boolean;
    color: string;
}

@Component({
    selector: 'app-rules-dashboard',
    templateUrl: './rules-dashboard.component.html',
    styleUrls: ['./rules-dashboard.component.scss'],
    standalone: false,
})
export class RulesDashboardComponent implements OnInit {
    public loggedIn!: boolean;
    title = 'Rules Dashboard';
    loading = false;
    displayError = false;
    errorMessage = '';

    ruleStatistics: RuleStatistics[] = [];
    filteredRuleStatistics: RuleStatistics[] = [];
    ruleTypeFilters: RuleTypeFilter[] = [];
    
    // Sorting
    sortColumn: string = '';
    sortDirection: 'asc' | 'desc' = 'asc';
    
    // Chart.js configuration - Glass Violet Theme
    public barChartData: ChartData<'bar'> = {
        labels: [],
        datasets: [{
            label: 'Number of Rules',
            data: [],
            backgroundColor: [],
            borderColor: [],
            borderWidth: 2,
            borderRadius: 10,
            barThickness: 40
        }]
    };

    public barChartOptions: ChartConfiguration['options'] = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                backgroundColor: 'rgba(102, 126, 234, 0.95)',
                borderColor: 'rgba(118, 75, 162, 0.8)',
                borderWidth: 2,
                padding: 14,
                cornerRadius: 8,
                titleFont: {
                    size: 14,
                    weight: 'bold',
                    family: 'system-ui'
                },
                bodyFont: {
                    size: 13,
                    family: 'system-ui'
                },
                titleColor: 'rgba(255, 255, 255, 1)',
                bodyColor: 'rgba(255, 255, 255, 0.95)',
                callbacks: {
                    label: (context) => {
                        return ` ${context.parsed.y} rules`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 5,
                    font: {
                        size: 12,
                        family: 'system-ui'
                    },
                    color: 'rgba(102, 126, 234, 0.8)'
                },
                grid: {
                    color: 'rgba(102, 126, 234, 0.1)',
                    lineWidth: 1
                },
                border: {
                    display: false
                }
            },
            x: {
                ticks: {
                    font: {
                        size: 11,
                        weight: 600,
                        family: 'system-ui'
                    },
                    color: 'rgba(102, 126, 234, 0.9)'
                },
                grid: {
                    display: false
                },
                border: {
                    display: false
                }
            }
        }
    };

    constructor(
        private rulesService: RulesService,
        private authService: AuthService,
        private toastService: AppToastService,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.loggedIn = this.isLoggedIn();
        this.loadRuleStatistics();
    }

    private isLoggedIn(): boolean {
        return !!this.authService.getToken();
    }

    public logout(): void {
        this.authService.logout();
    }

    loadRuleStatistics(): void {
        this.loading = true;
        const params: QueryParams = { page: 1, pageSize: 50 }; // Get up to 50 items for statistics

        // Load statistics for all rule types using forkJoin (modern RxJS approach)
        forkJoin([
            this.rulesService.getAdditionalRules(params),
            this.rulesService.getDrainageVentilationRules(params),
            this.rulesService.getGlazingRules(params),
            this.rulesService.getIntegratedRules(params),
            this.rulesService.getJointRules(params),
            this.rulesService.getLimitationRules(params),
            this.rulesService.getMatingRules(params)
        ]).subscribe({
            next: (responses) => {
                // Use totalCount first, then fallback to pagination.totalItems
                // Handle both camelCase and PascalCase responses
                this.ruleStatistics = [
                    { ruleType: 'Additional Rules', totalCount: this.getTotalCount(responses[0]) },
                    { ruleType: 'Drainage Ventilation Rules', totalCount: this.getTotalCount(responses[1]) },
                    { ruleType: 'Glazing Rules', totalCount: this.getTotalCount(responses[2]) },
                    { ruleType: 'Integrated Rules', totalCount: this.getTotalCount(responses[3]) },
                    { ruleType: 'Joint Rules', totalCount: this.getTotalCount(responses[4]) },
                    { ruleType: 'Limitation Rules', totalCount: this.getTotalCount(responses[5]) },
                    { ruleType: 'Mating Rules', totalCount: this.getTotalCount(responses[6]) }
                ];
                this.filteredRuleStatistics = [...this.ruleStatistics];
                this.applySorting();

                // Debug logging
                console.log('Rule Statistics:', this.ruleStatistics);
                console.log('Max count:', Math.max(...this.ruleStatistics.map(s => s.totalCount)));
                
                this.updateChartData();
                this.loading = false;
            },
            error: (error) => {
                console.error('Error loading rule statistics:', error);
                this.showError('Could not load rule statistics.');
                this.loading = false;
            }
        });
    }

    private getTotalCount(response: any): number {
        if (!response) return 0;
        // Try totalCount first (direct property), then pagination.totalItems, then fallback to 0
        return response.totalCount || 
               response.TotalCount || 
               response.pagination?.totalItems || 
               response.Pagination?.TotalItems || 
               0;
    }

    updateChartData(): void {
        const colors = [
            { bg: 'rgba(102, 126, 234, 0.7)', border: 'rgba(102, 126, 234, 1)' },
            { bg: 'rgba(105, 118, 222, 0.7)', border: 'rgba(105, 118, 222, 1)' },
            { bg: 'rgba(107, 109, 210, 0.7)', border: 'rgba(107, 109, 210, 1)' },
            { bg: 'rgba(110, 101, 198, 0.7)', border: 'rgba(110, 101, 198, 1)' },
            { bg: 'rgba(113, 92, 186, 0.7)', border: 'rgba(113, 92, 186, 1)' },
            { bg: 'rgba(115, 84, 174, 0.7)', border: 'rgba(115, 84, 174, 1)' },
            { bg: 'rgba(118, 75, 162, 0.7)', border: 'rgba(118, 75, 162, 1)' }
        ];

        // Initialize filter pills if not already done
        if (this.ruleTypeFilters.length === 0) {
            this.ruleTypeFilters = this.ruleStatistics.map((stat, index) => ({
                ruleType: stat.ruleType,
                selected: true,
                color: colors[index % colors.length].bg
            }));
        }

        // Filter data based on selected pills
        const selectedStats = this.ruleStatistics.filter((stat, index) => 
            this.ruleTypeFilters[index]?.selected !== false
        );

        const labels = selectedStats.map(stat => this.getShortRuleType(stat.ruleType));
        const data = selectedStats.map(stat => stat.totalCount);
        const backgroundColors = selectedStats.map((stat, index) => {
            const originalIndex = this.ruleStatistics.indexOf(stat);
            return colors[originalIndex % colors.length].bg;
        });
        const borderColors = selectedStats.map((stat, index) => {
            const originalIndex = this.ruleStatistics.indexOf(stat);
            return colors[originalIndex % colors.length].border;
        });

        this.barChartData = {
            labels: labels,
            datasets: [{
                label: 'Number of Rules',
                data: data,
                backgroundColor: backgroundColors,
                borderColor: borderColors,
                borderWidth: 2,
                borderRadius: 10,
                barThickness: 40
            }]
        };
    }

    toggleRuleTypeFilter(index: number): void {
        this.ruleTypeFilters[index].selected = !this.ruleTypeFilters[index].selected;
        
        // Ensure at least one filter is selected
        const hasSelection = this.ruleTypeFilters.some(f => f.selected);
        if (!hasSelection) {
            this.ruleTypeFilters[index].selected = true;
            return;
        }
        
        this.updateChartData();
    }

    getFilterColor(ruleType: string): string {
        const filter = this.ruleTypeFilters.find(f => f.ruleType === ruleType);
        return filter?.color || 'rgba(102, 126, 234, 0.7)';
    }

    getShortRuleType(ruleType: string): string {
        const mapping: { [key: string]: string } = {
            'Additional Rules': 'Additional',
            'Drainage Ventilation Rules': 'Drainage',
            'Glazing Rules': 'Glazing',
            'Integrated Rules': 'Integrated',
            'Joint Rules': 'Joint',
            'Limitation Rules': 'Limitation',
            'Mating Rules': 'Mating'
        };
        return mapping[ruleType] || ruleType;
    }

    navigateToRuleManagement(ruleType: string): void {
        const routeMap: { [key: string]: string } = {
            'Additional Rules': '/rules/additional',
            'Drainage Ventilation Rules': '/rules/drainage-ventilation',
            'Glazing Rules': '/rules/glazing',
            'Integrated Rules': '/rules/integrated',
            'Joint Rules': '/rules/joint',
            'Limitation Rules': '/rules/limitation',
            'Mating Rules': '/rules/mating'
        };

        const route = routeMap[ruleType];
        if (route) {
            this.router.navigate([route]);
        } else {
            this.toastService.show('Rule management page not found', {
                classname: 'bg-warning text-dark',
                delay: 3000
            });
        }
    }

    getTotalRules(): number {
        return this.ruleStatistics.reduce((total, stat) => total + stat.totalCount, 0);
    }

    getRuleTypesCount(): number {
        return this.ruleStatistics.length;
    }

    getCurrentDate(): string {
        return new Date().toLocaleDateString();
    }

    // Sorting methods
    onSort(column: string): void {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
        this.applySorting();
    }

    applySorting(): void {
        if (!this.sortColumn) {
            this.filteredRuleStatistics = [...this.ruleStatistics];
            return;
        }

        this.filteredRuleStatistics.sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (this.sortColumn) {
                case 'ruleType':
                    aValue = (a.ruleType || '').toLowerCase();
                    bValue = (b.ruleType || '').toLowerCase();
                    break;
                case 'count':
                    aValue = a.totalCount || 0;
                    bValue = b.totalCount || 0;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return this.sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return this.sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }

    getSortIcon(column: string): string {
        if (this.sortColumn !== column) {
            return 'bi-arrow-down-up';
        }
        return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
    }

    showError(message: string): void {
        this.displayError = true;
        this.errorMessage = message;
    }
}
